import { makeAutoObservable, values } from 'mobx'
import { TreeData, TreeSourcePosition, TreeDestinationPosition, ItemId } from 'tree-dnd'
import { HandbookItem, HandbookItemType, HandbookV2 } from 'types'
import { HandbooksStore } from 'stores/HandbooksStore'
import {
  createHandbookItem,
  CreateHandbookItemParams,
  HandbookItemParams,
  updateHandbookItem,
} from 'api/clients/HandbookClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { HandbookTreeItem } from './HandbookTreeItem'
import { isEqual, sortBy } from 'lodash'
import { isLegacyHandbook } from '../util'
import { pipe, replace, toLower } from 'lodash/fp'
import { Theme } from 'stores/Theme'

export class HandbookTreeStore {
  handbook: HandbookV2
  /**
   * The first custom theme in the ancestry, can be used as fallback when the
   * handbook has no custom theme.
   */
  ancestorsTheme?: Theme
  /**
   * The theme of the handbook.
   */
  theme?: Theme
  handbooksStore: HandbooksStore
  draggingId: ItemId | null = null
  editingItemId: ItemId | null = null
  isCreating: boolean = false

  constructor(handbook: HandbookV2, handbooksStore: HandbooksStore) {
    makeAutoObservable(this)
    this.handbook = handbook
    this.handbooksStore = handbooksStore
    if (!Theme.isNull(handbook.ancestors_theme)) {
      this.ancestorsTheme = new Theme(handbook.ancestors_theme)
    }
    // If there is a newer theme, use that, otherwise use the legacy theme color
    if (handbook.theme_color && Theme.isNull(handbook.theme)) {
      this.theme = Theme.fromLegacyTheme(handbook.theme_color)
    }
    if (!Theme.isNull(handbook.theme)) {
      this.theme = new Theme(handbook.theme)
    }
  }

  get applicableTheme() {
    return this.theme || this.ancestorsTheme
  }

  get childrenArray(): HandbookTreeItem[] {
    return sortBy(Object.values(Object.fromEntries(this.children)), 'position')
  }

  get children(): Map<string, HandbookTreeItem> {
    const map = new Map<string, HandbookTreeItem>()

    const items = values(this.handbooksStore.items).filter((item) => item.data.handbook.key === this.handbook.key)

    sortBy(items, 'position').forEach((item) => map.set(item.id, item))

    return map
  }

  get firstDocKey() {
    return this.childrenArray.find((item) => item.data.document?.key)?.data?.document?.key
  }

  get itemsArray(): HandbookTreeItem[] {
    return Array.from(this.items.values())
  }

  get items(): Map<string, HandbookTreeItem> {
    const map = new Map<string, HandbookTreeItem>()
    map.set(
      '0',
      new HandbookTreeItem(this.handbooksStore, {
        id: '0',
        data: {
          nested_handbook: this.handbook,
        },
      }),
    )

    const extract = (node: HandbookTreeItem) => {
      if (!node.isExpanded) {
        return
      }
      node._children.forEach((item) => {
        map.set(item.id, item)
        extract(item)
      })
    }

    this.children.forEach((item, key) => {
      map.set(key, item)
      extract(item)
    })

    return map
  }

  get tree(): TreeData {
    return {
      rootId: 0,
      items: Object.fromEntries(this.items),
    }
  }

  setIsCreating = (isCreating: boolean) => {
    this.isCreating = isCreating
  }

  onDragStart = (itemId: ItemId) => {
    this.draggingId = itemId
  }

  onDragEnd = (source: TreeSourcePosition, destination?: TreeDestinationPosition, beforeDragEnd?: () => void) => {
    if (!destination || !this.draggingId || isEqual(source, destination)) {
      return
    }

    const draggedItem = this.handbooksStore.items.get(this.draggingId.toString())
    const destinationId = destination.parentId.toString()

    const destinationHandbook =
      destinationId === '0'
        ? { key: this.handbook.key, id: this.handbook.id }
        : this.handbooksStore.items.get(destinationId)?.data.nested_handbook

    if (isLegacyHandbook(this.handbook)) {
      // DEPRECATE
      return
    }

    if (!destinationHandbook || !draggedItem) return

    if (beforeDragEnd) return beforeDragEnd()
    draggedItem.move(source.index, destinationHandbook.key, destination.index)
    this.draggingId = null
  }

  isFileInTree = (itemKey: string, itemType: HandbookItemType = HandbookItemType.Document) => {
    return this.itemsArray.some((item) => {
      if (item.data.type !== itemType) {
        return false
      }

      if (itemType === HandbookItemType.NestedHandbook && item.data.folder?.key === itemKey) {
        return true
      }

      return item.data.document?.key === itemKey
    })
  }

  isCoverDocItem = (handbook: HandbookV2, item: HandbookItem) => {
    if (!(item.document && handbook.cover_doc?.id === item.document.key)) return false
    // only if the doc is first in the tree, it can be treated as a cover doc
    const firstItemWithSameDoc = this.itemsArray.find((treeItem) => treeItem.data.document?.key === item.document?.key)
    return item.id === Number(firstItemWithSameDoc?.id)
  }

  toggleHandbookMode = (folderKey: string) => {
    const folder = this.itemsArray.find((treeItem) => treeItem.data.folder?.key === folderKey)
    if (!folder) return
    const wasHandbookMode = folder.data.handbook_mode
    folder.data.handbook_mode = !wasHandbookMode
  }

  toggleDatabaseMode = (folderKey: string) => {
    const folder = this.itemsArray.find((treeItem) => treeItem.data.folder?.key === folderKey)
    if (!folder) return
    const wasDatabaseMode = folder.data.database_mode
    folder.data.database_mode = !wasDatabaseMode
  }

  createItem = async (values: CreateHandbookItemParams) => {
    const response = await createHandbookItem({
      handbook_id: this.handbook.id,
      ...values,
    })

    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      throw new Error()
    }

    const newItem = response.data.handbook_item
    this.handbooksStore.addItem(newItem)
    this.handbooksStore.items.get(newItem.id.toString())?.insertAt(newItem.position)

    return newItem
  }

  updateItem = async (item: Pick<HandbookItem, 'id' | 'parent_id'>, values: HandbookItemParams) => {
    const response = await updateHandbookItem(item.id, values)
    if (response.type !== SUCCESS_RESPONSE_TYPE) {
      throw new Error()
    }

    const treeItem = this.handbooksStore.items.get(item.id.toString())
    if (!treeItem) return

    treeItem.updateAttrs({ ...response.data.handbook_item, parent_id: item.parent_id })

    return treeItem.data
  }

  get lastChild() {
    if (this.isEmpty) {
      return undefined
    }
    return this.childrenArray[this.childrenArray.length - 1]
  }

  get isEmpty() {
    return this.children.size === 0
  }

  get isLegacy() {
    return isLegacyHandbook(this.handbook)
  }

  get colorTheme() {
    return this.handbook.theme_color
  }

  get fontTheme() {
    if (!this.handbook.theme_font) {
      return undefined
    }
    return pipe(replace(/ /g, '-'), toLower)(this.handbook.theme_font)
  }

  refresh = () => {
    this.handbooksStore.refresh(this.handbook.key)
  }

  get isDatabase() {
    return Boolean(this.handbook.folder?.database_mode)
  }
}
